import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import CssTransition from "../../../component/layout/css-transition";
import {
  TextField,
  NumberField,
  Paragraph,
  Dropdown,
  Checkbox,
  Toggle,
  RadioButton,
  Link,
  DateTime,
} from "./CustomizedComponent";
import swal from "@sweetalert/with-react";
import CustomeModal from "../../../component/modal-popup/customeModal";
import {
  getMasterComponentList,
  downloadMasterComponent,
  getAllComponentList,
} from "../../../redux/actions/manageComponent";

function DownloadComponentModal({ modalOpen, setModalOpen }) {
  const [selected, setSelected] = useState([]);
  const [loader, setLoader] = useState(false);
  const [downloadLoader, setDownloadLoader] = useState(false);
  const dispatch = useDispatch();
  const { defaultComponentList } = useSelector(
    (state) => state.manageComponent
  );
  const { company } = useSelector((state) => state.auth);

  const getDefaultComponentList = () => {
    setLoader(true);
    const request = {
      onSuccess: () => {
        setLoader(false);
      },
      onError: () => {
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content="Unable to fetch component list"
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };
    dispatch(getMasterComponentList(request));
  };

  useEffect(() => {
    if (modalOpen) {
      getDefaultComponentList();
    }
  }, [modalOpen]);

  const getComponentList = () => {
    const request = {
      onError: () => {
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content="Unable to fetch component list"
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };
    dispatch(getAllComponentList(request));
  };

  const downloadComponent = () => {
    if(selected.length === 0){
      return swal({
        content: (
          <CustomeModal
            type="error"
            title="Error!"
            content="Please select atleast one component to download."
          />
        ),
        buttons: false,
        closeOnClickOutside: false,
        closeOnEsc: false,
      });
    }
    setDownloadLoader(true);
    const payload = {
      master_components_ids: selected,
      company_id: company.id,
    };
    const request = {
      onSuccess: (msg) => {
        swal({
          content: (
            <CustomeModal
              type="success"
              title="Success!"
              content="Component downloaded successfully"
              onClick={() => {
                setModalOpen(!modalOpen);
                setSelected([]);
              }}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
        getComponentList();
        setDownloadLoader(false);
      },
      onError: () => {
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content="Unable to download component. Please try again later."
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
        setDownloadLoader(false);
      },
      payload,
    };
    dispatch(downloadMasterComponent(request));
  };

  // Function To get component based on type
  const getComponent = (component, index) => {
    const componentObj = {
      componentType: component.type,
      componentId: component.component_id,
      componentName: component.label,
      componentPlaceholder: component.placeholder,
      componentTooltip: component.tooltip,
      isCheckedByDefault: component.is_check_by_default,
      validation: component.validation_api_id,
      minimumValue: component.min,
      maximumValue: component.max,
      component_special_id: component.component_name,
      priorDate: component.prior_date,
      futureDate: component.future_date,
      link_url: component.url,
    };
    switch (component.type) {
      case "text":
        return (
          <TextField
            componentParameter={componentObj}
            isSelected={selected.includes(component.master_component_id)}
            isDisabled={component.imported}
          />
        );
      case "number":
        return (
          <NumberField
          componentParameter={componentObj}
            isSelected={selected.includes(component.master_component_id)}
            isDisabled={component.imported}
          />
        );
      case "paragraph":
        return (
          <Paragraph
          componentParameter={componentObj}
            isSelected={selected.includes(component.master_component_id)}
            isDisabled={component.imported}
          />
        );
      case "dropdown":
        return (
          <Dropdown
          componentParameter={componentObj}
            isDisabled={component.imported}
            isSelected={selected.includes(component.master_component_id)}
            dropdownOptions={
              component.list_values
                ? JSON.parse(component.list_values).map((component) => {
                    const obj = {
                      label: component,
                      value: component,
                    };
                    return obj;
                  })
                : []
            }
          />
        );
      case "check box":
        return (
          <Checkbox
          componentParameter={componentObj}
            isDisabled={component.imported}
            isSelected={selected.includes(component.master_component_id)}
          />
        );
      case "toggle":
        return (
          <Toggle
          componentParameter={componentObj}
            isDisabled={component.imported}
            isSelected={selected.includes(component.master_component_id)}
          />
        );
      case "radio group":
        return (
          <RadioButton
          componentParameter={componentObj}
            isDisabled={component.imported}
            isSelected={selected.includes(component.master_component_id)}
            dropdownOptions={
              component.list_values
                ? JSON.parse(component.list_values).map((component) => {
                    const obj = {
                      label: component,
                      value: component,
                    };
                    return obj;
                  })
                : []
            }
          />
        );
      case "link":
        return (
          <Link
          componentParameter={componentObj}
            isDisabled={component.imported}
            isSelected={selected.includes(component.master_component_id)}
          />
        );
      case "date/time":
        return (
          <DateTime
            componentParameter={componentObj}
            isDisabled={component.imported}
            isSelected={selected.includes(component.master_component_id)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <CssTransition
        className="fixed inset-0 bg-slate-900 bg-opacity-30 z-50 transition-opacity"
        show={modalOpen}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        aria-hidden="true"
      />
      <CssTransition
        className="fixed inset-0 z-50 overflow-hidden flex items-center my-4 justify-center px-4 sm:px-6"
        role="dialog"
        aria-modal="true"
        show={modalOpen}
        enter="transition ease-in-out duration-200"
        enterStart="opacity-0 translate-y-4"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveStart="opacity-100 translate-y-0"
        leaveEnd="opacity-0 translate-y-4"
      >
        <div className="bg-white overflow-auto max-w-4xl w-full max-h-full rounded-2xl shadow-lg flex flex-col relative">
          <div className="py-8 px-6 space-y-5">
            <div className="space-y-2">
              <h4 className="text-base leading-5 text-[#1E293B] font-semibold text-center">
                Download Existing Components
              </h4>
              <div>
                <p className="text-sm text-center text-[#888888] leading-5">
                  Please select the components you want to download.
                </p>
              </div>
            </div>
            {/* ---- Component Cards ---- */}
            {!loader ? (
              <div className="h-[370px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2 overflow-y-auto">
                {defaultComponentList?.length > 0
                  ? defaultComponentList.map((component, index) => {
                      return (
                        <div
                          className={`h-28 rounded-md ${
                            selected.includes(component.master_component_id)
                              ? "bg-[--bg-color-500]"
                              : "bg-[#F5F9FF]"
                          }  px-4 flex justify-center items-center ${
                            component.imported
                              ? "pointer-events-none"
                              : "cursor-pointer"
                          } cursor-pointer overflow-y-auto relative`}
                          onClick={() => {
                            if (
                              selected.includes(component.master_component_id)
                            ) {
                              const filteredArray = selected.filter(
                                (element) =>
                                  element !== component.master_component_id
                              );
                              setSelected(filteredArray);
                            } else {
                              setSelected([
                                ...selected,
                                component.master_component_id,
                              ]);
                            }
                          }}
                        >
                          <span
                            className={`fa-solid fa-badge-check text-lg absolute top-2 right-2 text-[--bg-color-500] ${
                              component.imported ? "block" : "hidden"
                            } `}
                          ></span>
                          {getComponent(component, index)}
                        </div>
                      );
                    })
                  : null}
              </div>
            ) : (
              <div className="h-[370px] overflow-y-auto flex justify-center items-center">
                <svg
                  className="animate-spin w-12 h-12 fill-current shrink-0"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                </svg>
              </div>
            )}

            {/* ----- Footer ---- */}
            <div className="flex justify-center items-center">
              <div className="w-1/2 flex justify-between items-center space-x-4">
                <div className="w-1/2">
                  <button
                    className="w-full btn bg-white border border-slate-200 hover:border-slate-300 text-[#1E293B]"
                    onClick={() => {
                      setModalOpen(!modalOpen);
                      setSelected([]);
                    }}
                  >
                    Cancel
                  </button>
                </div>
                <div className="w-1/2">
                  <button
                    className={`w-full btn  text-white ${
                      downloadLoader ? "bg-[--bg-color-300]" : "bg-[--bg-color-500]"
                    }`}
                    onClick={() => downloadComponent()}
                  >
                    {downloadLoader ? (
                      <>
                        <span class="fa-solid fa-download fa-bounce text-white text-base mr-2"></span>
                        Downloading...
                      </>
                    ) : (
                      "Download"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CssTransition>
    </>
  );
}

export default DownloadComponentModal;
